import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    takeEvery,
    takeLatest
} from 'redux-saga/effects';
import keyBy from 'lodash.keyby';
import set from 'lodash.set';
import get from 'lodash.get';
// @ts-ignore
import { REHYDRATE } from 'redux-persist';

// Global Types
import { SagaIterator } from 'redux-saga';

// Global Actions
// @ts-ignore
import { updateQueryString } from 'services/router/actions';
import { mergeUserEntityAssignments } from 'services/userEntityAssignments/actions';

// Global Constants
// @ts-ignore
import { SET_IS_ONLINE } from 'services/connection/constants';
import { QUERY_STRING_PARAM } from 'components/EntitiesSaveErrorModal/constants';

// Global Utils
import { putRequest } from 'utils/redux-saga';

// Global Selectors
// @ts-ignore
import { selectIsOnline } from 'services/connection/selectors';

// Types
import { EntityGroupedUserAssignment } from 'services/userEntityAssignments/types';
import {
    EntitiesValueUpdateAction,
    EntityValueUpdateAction,
    Entity
} from './types';

// Local Actions
import {
    requestSaveEntities,
    succeededSaveEntities,
    failedSaveEntities,
    updateEntities,
    updateEntity,
    mergeEntities,
    setEntityErrors
} from './actions';

// Local Selectors
import {
    selectUnsavedEntities,
    makeSelectEntity,
    selectEntitiesKeyedByUuid
} from './selectors';

// Local Constants
import {
    ENTITIES_SAVE_REQUESTED,
    ENTITIES_UPDATE,
    ENTITY_UPDATE,
    ENTITIES_VALUE_UPDATE,
    ENTITY_VALUE_UPDATE,
    ENTITY_REMOVE,
    ENTITIES_REMOVE
} from './constants';

// Local Utils
import { formatEntities, formatEntityPayloads } from './utils';

export function* saveEntities(): SagaIterator {
    yield delay(1000);

    const unsavedEntities = yield select(selectUnsavedEntities);

    try {
        const response = yield call(putRequest, 'Entities/bulk', {
            ...unsavedEntities,
            create: formatEntityPayloads(unsavedEntities.create),
            update: formatEntityPayloads(unsavedEntities.update)
        });

        yield put(succeededSaveEntities(response));

        const deletedEntities = response.data.delete;
        const createdEntities: Entity[] = response.data.create;

        const errors = get(response, ['data', 'errors'], []);

        if (deletedEntities) {
            const createdEntitiesKeyedByUuid = keyBy(
                formatEntities(deletedEntities),
                'uuid'
            );

            yield put(mergeEntities(createdEntitiesKeyedByUuid));
        }

        if (createdEntities) {
            const createdEntitiesKeyedByUuid = keyBy(
                formatEntities(createdEntities),
                'uuid'
            );

            yield put(mergeEntities(createdEntitiesKeyedByUuid));

            const updatedUserEntityAssignments: EntityGroupedUserAssignment[] = createdEntities.map(
                (entity) => ({
                    entityId: entity.id as number,
                    userAssignments: entity.assignedUsers
                        ? entity.assignedUsers.map((assignedUser) => ({
                              email: assignedUser.email,
                              jobType: assignedUser.jobType
                          }))
                        : []
                })
            );

            yield put(mergeUserEntityAssignments(updatedUserEntityAssignments));
        }

        yield put(setEntityErrors(errors));

        if (errors.length) {
            yield put(failedSaveEntities(response));

            yield put(
                updateQueryString({
                    [QUERY_STRING_PARAM]: true
                })
            );
        }
    } catch (error) {
        yield put(failedSaveEntities(error));

        yield put(
            updateQueryString({
                [QUERY_STRING_PARAM]: true
            })
        );
    }
}

export function* watchEntitiesSaveRequested(): SagaIterator {
    yield takeLatest(ENTITIES_SAVE_REQUESTED, saveEntities);
}

export function* triggerSaveEntities(): SagaIterator {
    const isOnline = yield select(selectIsOnline);
    const unsavedEntities = yield select(selectUnsavedEntities);

    if (isOnline && unsavedEntities) {
        yield put(requestSaveEntities());
    }
}

/**
 * watch for any actions that we want to trigger a save
 */
export function* watchEntitiesSaveTriggers(): SagaIterator {
    yield takeLatest(
        [
            REHYDRATE,
            ENTITIES_UPDATE,
            ENTITY_UPDATE,
            ENTITY_REMOVE,
            ENTITIES_REMOVE,
            SET_IS_ONLINE
        ],
        triggerSaveEntities
    );
}

export function* setValueOnEntities(
    action: EntitiesValueUpdateAction
): SagaIterator {
    const {
        payload: { entityUuids, field, value }
    } = action;

    const nextEntities = [];
    const entityUuidsLength = entityUuids.length;

    const entities = yield select(selectEntitiesKeyedByUuid);

    for (let index = 0; index < entityUuidsLength; index += 1) {
        const entity = entities[entityUuids[index]];

        nextEntities.push({
            ...entity,
            [field]: value
        });
    }

    yield put(updateEntities(nextEntities));
}

export function* watchEntitiesValueUpdate(): SagaIterator {
    yield takeEvery(ENTITIES_VALUE_UPDATE, setValueOnEntities);
}

export function* setValueOnEntity(
    action: EntityValueUpdateAction
): SagaIterator {
    const {
        payload: { entityUuid, field, value }
    } = action;

    const entity = yield select(makeSelectEntity(), {
        entityUuid
    });

    // Create a deep clone of the entity
    const nextEntity = JSON.parse(JSON.stringify(entity));

    set(nextEntity, field, value);

    yield put(updateEntity(nextEntity));
}

export function* watchEntityValueUpdate(): SagaIterator {
    yield takeEvery(ENTITY_VALUE_UPDATE, setValueOnEntity);
}

export default function* entitiesSagas(): SagaIterator {
    yield all([
        fork(watchEntitiesSaveRequested),
        fork(watchEntitiesSaveTriggers),
        fork(watchEntitiesValueUpdate),
        fork(watchEntityValueUpdate)
    ]);
}
